import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Hourglass, X, Zap } from "lucide-react";

const DISMISS_KEY = "hg_trial_dismiss_until";

/**
 * Free-trial-ending banner — fires from 3 days before `trial_ends_at` until
 * the trial actually expires (then the paywall takes over).
 *
 * Mirrors RenewalBanner in style + dismiss behavior (12h snooze), but the
 * CTA points to /pricing because the user hasn't paid yet. Stage drives
 * urgency: `trial_3d` is friendly, `trial_1d` is the final nudge.
 */
export default function TrialEndingBanner() {
    const navigate = useNavigate();
    const [status, setStatus] = useState(null);
    const [dismissedUntil, setDismissedUntil] = useState(() => {
        const v = localStorage.getItem(DISMISS_KEY);
        return v ? parseInt(v, 10) : 0;
    });

    useEffect(() => {
        let alive = true;
        api.get("/payments/trial-status")
            .then((r) => { if (alive) setStatus(r.data); })
            .catch(() => { if (alive) setStatus(null); });
        return () => { alive = false; };
    }, []);

    const visible = useMemo(() => {
        if (!status?.should_remind) return false;
        if (Date.now() < dismissedUntil) return false;
        return true;
    }, [status, dismissedUntil]);

    const dismiss = () => {
        // 12-hour snooze — matches RenewalBanner.
        const next = Date.now() + 12 * 60 * 60 * 1000;
        localStorage.setItem(DISMISS_KEY, String(next));
        setDismissedUntil(next);
    };

    if (!visible) return null;

    const days = Math.max(0, status.days_remaining ?? 0);
    const dayLabel = days === 0
        ? (status.hours_remaining && status.hours_remaining > 0 ? `in ${status.hours_remaining} hours` : "today")
        : days === 1 ? "in 1 day" : `in ${days} days`;
    const isFinalDay = status.stage === "trial_1d";

    const eyebrow = isFinalDay ? "Last day of trial" : "Trial ending soon";
    const tone = isFinalDay
        ? "Pick a plan now — your workouts, PRs, and rank progression stay locked in."
        : "If Hypergains is working, locking a plan now means zero disruption when the trial closes.";

    const containerCls = isFinalDay
        ? "relative w-full border-b-2 border-accent-red bg-gradient-to-r from-[#1a0606] via-[#0f0303] to-[#1a0606] text-white"
        : "relative w-full border-b border-accent-red/50 bg-gradient-to-r from-[#101418] via-[#0a0d10] to-[#101418] text-white";

    return (
        <div
            data-testid="trial-banner"
            data-stage={status.stage || ""}
            className={containerCls}
        >
            <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6">
                <div className="hidden flex-none rounded-sm border border-accent-red/40 bg-accent-red/15 p-2 sm:block">
                    <Hourglass className={`h-5 w-5 ${isFinalDay ? "text-accent-red" : "text-accent-red/70"}`} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span className="label-eyebrow text-accent-red">{eyebrow}</span>
                        <span className="text-xs text-white/60">7-day free trial</span>
                    </div>
                    <p className="mt-0.5 text-sm font-medium leading-snug sm:text-base">
                        Trial ends <span className="font-bold text-accent-red" data-testid="trial-days">{dayLabel}</span>.
                        <span className="ml-1 text-white/75">{tone}</span>
                    </p>
                </div>
                <div className="flex flex-none items-center gap-2">
                    <button
                        onClick={() => navigate("/pricing?from=trial")}
                        data-testid="trial-banner-cta"
                        className="flex items-center gap-1.5 rounded-sm bg-accent-red px-4 py-2 text-xs font-bold uppercase tracking-wider text-white shadow-[0_0_0_0_rgba(239,68,68,0)] transition-all hover:bg-[color:var(--accent-hover)] hover:shadow-[0_0_24px_-4px_rgba(239,68,68,0.7)]"
                    >
                        <Zap className="h-3.5 w-3.5" /> {isFinalDay ? "Start a plan now" : "Start a plan"}
                    </button>
                    <button
                        onClick={dismiss}
                        aria-label="Dismiss for 12 hours"
                        data-testid="trial-banner-dismiss"
                        className="rounded-sm border border-white/15 p-2 text-white/70 hover:bg-white/5 hover:text-white"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
}
